import { useCallback, useEffect, useRef, useState } from 'react';

// Reusable roving-tabindex / arrow-key controller for an accessible
// single-select radiogroup (ENG-5256 Account Overview stat cards). Linear arrow
// navigation across all options with wrap, plus Home/End. The hook owns the
// roving focus index and exposes per-option props (tabIndex, aria-checked,
// keydown, ref) so the consumer only renders. Option count is not hardcoded —
// pass any list of stable ids.

interface UseRovingRadioGroupParams {
  /** Stable ids in DOM order. */
  ids: string[];
  /** The currently selected (checked) id. */
  selectedId: string;
  /** Fires when the user selects an option (Enter/Space/click or arrow move). */
  onSelect: (id: string) => void;
}

interface OptionProps {
  ref: (node: HTMLElement | null) => void;
  role: 'radio';
  tabIndex: 0 | -1;
  'aria-checked': boolean;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  onClick: () => void;
  onFocus: () => void;
}

interface UseRovingRadioGroupResult {
  /** Props to spread onto each option, keyed by id. */
  getOptionProps: (id: string) => OptionProps;
}

const NAV_KEYS = new Set(['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End']);

export function useRovingRadioGroup({
  ids,
  selectedId,
  onSelect,
}: UseRovingRadioGroupParams): UseRovingRadioGroupResult {
  // The roving index decides which option is in the tab order. It tracks the
  // selection by default but moves with arrow keys ahead of committing.
  const initialIndex = Math.max(0, ids.indexOf(selectedId));
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const nodes = useRef<Map<string, HTMLElement | null>>(new Map());

  // Keep the roving index pointed at the selection when it changes externally
  // (e.g. a click elsewhere or a programmatic select).
  useEffect(() => {
    const next = ids.indexOf(selectedId);
    if (next >= 0) setActiveIndex(next);
  }, [ids, selectedId]);

  const focusIndex = useCallback(
    (index: number) => {
      const id = ids[index];
      if (id === undefined) return;
      setActiveIndex(index);
      onSelect(id);
      nodes.current.get(id)?.focus();
    },
    [ids, onSelect]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>, index: number) => {
      if (!NAV_KEYS.has(event.key)) return;
      event.preventDefault();

      const last = ids.length - 1;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          focusIndex(index === last ? 0 : index + 1);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          focusIndex(index === 0 ? last : index - 1);
          break;
        case 'Home':
          focusIndex(0);
          break;
        case 'End':
          focusIndex(last);
          break;
      }
    },
    [ids, focusIndex]
  );

  const getOptionProps = useCallback(
    (id: string): OptionProps => {
      const index = ids.indexOf(id);
      const isActive = index === activeIndex;
      return {
        ref: (node: HTMLElement | null) => {
          nodes.current.set(id, node);
        },
        role: 'radio',
        // Roving tabindex: exactly one option (the active one) is tabbable.
        tabIndex: isActive ? 0 : -1,
        'aria-checked': id === selectedId,
        onKeyDown: (event) => handleKeyDown(event, index),
        onClick: () => {
          setActiveIndex(index);
          onSelect(id);
        },
        onFocus: () => setActiveIndex(index),
      };
    },
    [ids, activeIndex, selectedId, handleKeyDown, onSelect]
  );

  return { getOptionProps };
}
